import React, { useCallback, useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Button from '@mui/material/Button'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import BottomButtonGroup, { BUTTON_LOADING_ICON, FORM_DIALOG_ACTION_BUTTON_SX, FORM_DIALOG_DANGER_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import { renderDate } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'

const DEFAULT_MEDAL_ORDER_SEARCH_SELECT = {
    searchStatus: 0,
    searchPopupStatus: '',
}

export default function MedalOrder(props) {
    const history = useHistory()
    const printRef = useRef()
    const deleteInFlightRef = useRef(false)
    const id = props.match.params.medalOrderId
    const location = useLocation()
    const locationState = getLocationState(location)
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || DEFAULT_MEDAL_ORDER_SEARCH_SELECT
    const listSearch = location.search || ''
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [products, setProducts] = useState({})
    const [productSpecs, setProductSpecs] = useState([])
    const [productOptions, setProductOptions] = useState({})
    const [orderParams, setOrderParams] = useState({})
    const [amountPrams, setAmountPrams] = useState({})
    const [filePrams, setFilePrams] = useState([])
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')

    // 견적서 영역의 기존 "1,000 원" 표기 규칙을 유지한다.
    const formatEstimateCurrency = (value) => `${formatNumber(value)} 원`

    // 상세 조회 실패 시에도 견적서 조각 상태를 한 번에 비워 이전 데이터 노출을 막는다.
    const resetDetailState = useCallback((errorMessage = '') => {
        setProducts({})
        setProductSpecs([])
        setProductOptions({})
        setOrderParams({})
        setAmountPrams({})
        setFilePrams([])
        setHasDetail(false)
        setDetailErrorMessage(errorMessage)
    }, [])

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 메달 주문 상세를 불러올 수 없습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/medal/getProductOrder', { medalOrderId: id })

                if (data.code === 200) {
                    const detail = data.data || {}
                    const hasMedalOrder = Boolean(detail?.medal_order?.medalOrderId)

                    setProducts(detail)
                    setProductSpecs(detail?.medal_product_spec || [])
                    setProductOptions(detail?.medal_product_option || {})
                    setOrderParams(detail?.medal_order || {})
                    setAmountPrams(detail?.amount_info || {})
                    setFilePrams(detail?.medal_order_image || [])
                    setHasDetail(hasMedalOrder)
                    setDetailErrorMessage(hasMedalOrder ? '' : '메달 주문 상세 정보를 찾을 수 없습니다.')
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '메달 주문 상세를 불러오지 못했습니다.'
                    resetDetailState(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '메달 주문 상세를 불러오지 못했습니다.'
                resetDetailState(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }

        getData()
    }, [history, id, resetDetailState])

    // 삭제 후에도 운영자가 보던 메달 주문 목록 조건으로 그대로 복귀시킨다.
    const moveToList = (snackbarState) => {
        history.push(createListLinkTarget({
            pathname: '/medalOrders',
            search: listSearch,
            curPage: curPage,
            searchText: searchText,
            searchSelect: searchSelect,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleDelete = async () => {
        if (deleteInFlightRef.current) {
            return
        }

        try {
            deleteInFlightRef.current = true
            setDeleting(true)

            const { data } = await API.post('/api/v1/admin/medal/deleteProductOrder', { medalOrderId: id })

            if (data.code === 200) {
                moveToList({ severity: 'success', message: '메달 주문을 삭제했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('메달 주문을 삭제하지 못했습니다.')
        } finally {
            deleteInFlightRef.current = false
            setDeleting(false)
        }
    }

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: '메달견적서',
    })

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="메달 주문 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 1 }}
        />
    )

    const renderRow = (label, value) => (
        <Grid container sx={{ mb: 1 }}>
            <Grid item xs={3}>
                <Typography variant="body2" fontWeight="bold">{label}</Typography>
            </Grid>
            <Grid item xs={9}>
                <Typography variant="body2" textAlign="right">{value || '-'}</Typography>
            </Grid>
        </Grid>
    )

    if (loading) {
        return (
            <>
                <Grid item xs={12}>
                    <Spiner/>
                </Grid>
                {openSnackbar && (
                    <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>
                )}
            </>
        )
    }

    if (!hasDetail) {
        return (
            <>
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="h5" fontWeight="bold">견적서</Typography>
                        {renderDetailErrorState()}
                    </Paper>
                </Grid>
                {openSnackbar && (
                    <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>
                )}
            </>
        )
    }

    return (
        <>
            <Grid item xs={12} ref={printRef} id="estimate-area">
                <Paper
                    sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'break-word',
                        overflow: 'hidden',
                        opacity: deleting ? 0.6 : 1,
                    }}
                >
                    <Box sx={{ mb: 2 }}>
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item>
                                <Typography variant="body1" fontWeight="bold">{renderDate(orderParams?.regTime)}</Typography>
                                <Typography variant="h5" fontWeight="bold">견적서</Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant="body2" color="text.secondary">문의번호 {orderParams.medalOrderId}</Typography>
                            </Grid>
                        </Grid>
                        <Divider sx={{ mt: 1 }} />
                    </Box>

                    <Box>
                        <Typography variant="h6" gutterBottom>상품 견적</Typography>

                        <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {products.title}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                옵션:{' '}
                                {orderParams.isDesign ? '디자인 있음' : '디자인 없음'},{' '}
                                {orderParams.isPolish ? '유광' : '무광'},{' '}
                                {orderParams.packingType === 1 ? '개별포장' : '개별맞춤케이스'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 2 }}>
                        {productSpecs.map((spec, idx) => (
                            <Grid container key={idx} sx={{ mb: 1 }}>
                                <Grid item xs={3}>
                                    <Typography variant="body2">지름 {spec.diameter} cm</Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography variant="body2" textAlign="right">{spec.quantity} 개</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" textAlign="right">{formatEstimateCurrency(spec.amount)}</Typography>
                                </Grid>
                            </Grid>
                        ))}
                        {productOptions && productOptions?.medalProductOptionId && (
                            <Grid container sx={{ mb: 1 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" sx={{ mt: 1 }}>+ {productOptions.optionName} {formatEstimateCurrency(productOptions.price)} / 개당</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" sx={{ mt: 1 }} textAlign="right">{formatEstimateCurrency(amountPrams.addOptionAmount)}</Typography>
                                </Grid>
                            </Grid>
                        )}
                    </Box>

                    <Box>
                        <Typography variant="h6" gutterBottom>예상 견적 금액</Typography>
                        <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 1 }}>
                            <Grid container sx={{ mb: 1 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" fontWeight="bold">총 상품 금액</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.totalAmount)}</Typography>
                                </Grid>
                            </Grid>
                            <Grid container sx={{ mb: 1 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" fontWeight="bold">추가 금액</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.addAmount)}</Typography>
                                </Grid>
                            </Grid>
                            {amountPrams.addMoldAmount > 0 && (
                                <Grid container sx={{ pl: 2, mb: 1 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2">금형 비용</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.addMoldAmount)}</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {amountPrams.addDesingAmount > 0 && (
                                <Grid container sx={{ pl: 2, mb: 1 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2">디자인 비용</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.addDesingAmount)}</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {amountPrams.addPackingAmount > 0 && (
                                <Grid container sx={{ pl: 2, mb: 1 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2">개별 케이스 비용</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.addPackingAmount)}</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            <Grid container sx={{ mb: 1 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" fontWeight="bold">할인 금액</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.discount)}</Typography>
                                </Grid>
                            </Grid>
                            {amountPrams.discountQuantityAmount > 0 && (
                                <Grid container sx={{ pl: 2, mb: 1 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2">1000개 이상 구매 {products.quantityDiscount}%</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.discountQuantityAmount)}</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {amountPrams.discountMoldAmount > 0 && (
                                <Grid container sx={{ pl: 2, mb: 1 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2">금형 비용</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.discountMoldAmount)}</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {amountPrams.discountDesingAmount > 0 && (
                                <Grid container sx={{ pl: 2, mb: 1 }}>
                                    <Grid item xs={6}>
                                        <Typography variant="body2">디자인 비용</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.discountDesingAmount)}</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            <Divider sx={{ mb: 1 }}/>
                            <Grid container sx={{ mb: 1 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" fontWeight="bold">예상 금액</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" textAlign="right">{formatEstimateCurrency(amountPrams.estimatedPrice)}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="h6" gutterBottom>제작 문의</Typography>
                        <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 1 }}>
                            {renderRow('납품 희망일', orderParams.deliveryDate)}
                            {renderRow('샘플 신청 여부', orderParams.isSample === 1 ? '신청함' : '신청안함')}
                            {renderRow('문의 내용', orderParams.content)}
                            <Grid container sx={{ mb: 1 }}>
                                <Grid item xs={3}>
                                    <Typography variant="body2" fontWeight="bold">첨부파일</Typography>
                                </Grid>
                                <Grid item xs={9}>
                                    {filePrams && filePrams.length > 0 ? (
                                        filePrams.map((file, idx) => {
                                            const fileName = file.image.split('/').pop()
                                            return (
                                                <Typography
                                                    textAlign="right"
                                                    key={idx}
                                                    variant="body2"
                                                    sx={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline', mb: 0.5 }}
                                                    onClick={() => {
                                                        const link = document.createElement('a')
                                                        link.href = `${process.env.REACT_APP_AWS_S3_URL}/${file.image}`
                                                        link.download = fileName
                                                        document.body.appendChild(link)
                                                        link.click()
                                                        document.body.removeChild(link)
                                                    }}
                                                >
                                                    {fileName}
                                                </Typography>
                                            )
                                        })
                                    ) : (
                                        <Typography variant="body2" textAlign="right">-</Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="h6" gutterBottom>문의자 정보</Typography>
                        <Box sx={{ border: '1px solid #ccc', borderRadius: 2, p: 1 }}>
                            {renderRow('문의자 성함', orderParams.username)}
                            {renderRow('단체/회사명', orderParams.company)}
                            {renderRow('휴대폰 번호', orderParams.phone)}
                            {renderRow('이메일', orderParams.email)}
                        </Box>
                    </Box>
                </Paper>
            </Grid>
            {!loading && (
                <Grid container spacing={2} sx={{ mt: 2, pl: 3 }} justifyContent="center">
                    <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ '& button': { m: 2 } }}
                    >
                        <Button
                            type="button"
                            variant="outlined"
                            color="secondary"
                            sx={FORM_DIALOG_ACTION_BUTTON_SX}
                            onClick={() => moveToList()}
                            disabled={deleting}
                        >
                            뒤로
                        </Button>
                        <Button variant="contained" color="success" sx={FORM_DIALOG_ACTION_BUTTON_SX} onClick={handlePrint} disabled={deleting}>인쇄</Button>
                        <Button
                            variant="contained"
                            color="error"
                            sx={FORM_DIALOG_DANGER_ACTION_BUTTON_SX}
                            onClick={handleDelete}
                            disabled={deleting}
                            startIcon={deleting ? BUTTON_LOADING_ICON : undefined}
                            aria-busy={deleting}
                        >
                            {deleting ? '삭제 중...' : '삭제'}
                        </Button>
                    </Box>
                </Grid>
            )}

            {openSnackbar && (
                <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>
            )}
        </>
    )
}
